import React, { useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import {
  AddAction,
  Button,
  EmptyStateProps,
  MobileSearchField,
  Modal,
  Pagination,
  failedEmptyState,
  searchEmptyState,
  useIsMobile,
  useLoadPhase,
} from "../../../components/ui";
// Only for the first-run state below — see `EmptyState`'s `welcome` variant.
import { ReactComponent as FirstPlanMark } from "../../../assets/svg/No data images/plans.svg";
import { usePlanOptions } from "../hooks/usePlanOptions";
import { usePlanSave } from "../hooks/usePlanSave";
import { usePlans } from "../hooks/usePlans";
import { BrandPlan, NewBrandPlan } from "../types/brand.types";
import PlanCards from "./PlanCards";
import PlanFormModal from "./PlanFormModal";
import PlansTable from "./PlansTable";

/** How many rows a page of the table holds, from `md` up. */
const PLANS_PER_PAGE = 10;

/**
 * How many cards a phone lays out at a time.
 *
 * There are no pages on a phone — walking the list there is scrolling — so a
 * batch is how much of it is built into the page at once, and the next batch
 * is laid out while there is still a screenful above it. The same number the
 * Revenue list works in; see `useEndReached`.
 */
const CARDS_PER_BATCH = 20;

/**
 * Plans the brand offers, listed the same way My clients lists clients:
 * one card, a sticky-header table that scrolls on its own, and a pinned
 * pagination footer.
 *
 * On a phone it is laid out the way Revenue is instead, and for the same
 * reasons: no card around the list — the plans are cards of their own on the
 * page's own background — a search field shaped for a thumb, and no pager
 * under it, since walking a list on a phone is scrolling. Both dialogs are the
 * same either way.
 */
const BrandPlansTab: React.FC = () => {
  const { plans, status, reload, setPlans } = usePlans();

  // Rendered rather than hidden: a table and a list of cards holding the same
  // rows would be the same plans twice in the accessibility tree, and the
  // menus inside the hidden half would still be reachable by keyboard.
  const isMobile = useIsMobile();

  // A list that lands quickly goes straight from blank to filled: the table's
  // placeholders are only reached once the fetch has visibly stalled.
  const phase = useLoadPhase(status === "loading");
  const [search, setSearch] = useState("");

  // Where the table is, from `md` up.
  const [page, setPage] = useState(1);

  // How many cards a phone has laid out. It only ever grows, and it grows
  // ahead of the reader — see `PlanCards`.
  const [shown, setShown] = useState(CARDS_PER_BATCH);

  // The form dialog is open when `editing` is set: `null` creates, a plan
  // edits. Keeping one flag means the two states can't both be live.
  const [editing, setEditing] = useState<BrandPlan | null | undefined>(
    undefined
  );
  // The plan whose listing is being confirmed. Which way it is going is
  // read off its current status rather than stored: one flag cannot disagree
  // with the row it came from.
  const [pendingToggle, setPendingToggle] = useState<BrandPlan | null>(null);
  /**
   * The plan whose record is open in the phone's sheet — see `PlanCards`,
   * which draws it. `null` is the sheet shut, and it stays that way from `md`
   * up, where the table has no such panel.
   *
   * Held here rather than in the list because the sheet is a step on the way
   * to the form and to the confirmation: both take it down on the way out,
   * and both put it back on the way in again.
   */
  const [selected, setSelected] = useState<BrandPlan | null>(null);

  /**
   * Back to the record the phone was on, where there was one.
   *
   * The form is read as a step into the sheet rather than a jump out of it:
   * the plan was opened and then edited, so leaving the form — saved or
   * cancelled — lands on the plan again and not on the list it was found in.
   * The listing change needs none of this; it never leaves the sheet.
   *
   * Nothing from `md` up: there is no sheet there to come back to, and the
   * table's row menu was never inside one.
   */
  const stepBackTo = (plan: BrandPlan | null) => {
    if (isMobile) setSelected(plan);
  };

  // Fetched here rather than inside the dialog, and once when the tab opens:
  // the dialog is mounted for the whole life of the tab, so loading them there
  // would tie a request to nothing in particular, and reloading them on each
  // open would put two on the wire every time someone pressed Add plan.
  const { suggestions, durations } = usePlanOptions();
  const { saving, create, update, setActive } = usePlanSave();

  // Filtering and paging are local because the list endpoint takes neither a
  // search term nor a sort, so `usePlans` fetches the lot. The day it takes
  // either, this memo becomes the fetch and `page`/`search` become its params.
  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return plans;
    return plans.filter(
      (plan) =>
        plan.name.toLowerCase().includes(query) ||
        plan.duration.toLowerCase().includes(query)
    );
  }, [plans, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(filtered.length / PLANS_PER_PAGE)
  );
  // Clamp rather than reset: a filter that shrinks the list past the current
  // page should land on the last page, not throw away the user's position.
  const currentPage = Math.min(page, totalPages);

  // A page of the table, or as much of the list as the phone has laid out.
  const visible = isMobile
    ? filtered.slice(0, shown)
    : filtered.slice(
        (currentPage - 1) * PLANS_PER_PAGE,
        currentPage * PLANS_PER_PAGE
      );

  /** Whether the phone is holding back plans it already has. */
  const hasMore = shown < filtered.length;

  /** The next batch of cards. Safe to call twice for the same one — the list
   *  asks as its end comes into view, which can happen on consecutive scroll
   *  events. */
  const showMore = () =>
    setShown((current) => Math.min(current + CARDS_PER_BATCH, filtered.length));

  /** A narrower list is a different list, so it is read from the top —
   *  whichever of the two ways this tab is being read. */
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
    setShown(CARDS_PER_BATCH);
  };

  /** What the table shows in place of rows, whichever way it came to have none.
   *  Nothing about loading: while the list is out the table shimmers a page of
   *  rows, and the status line below is what says so in words. */
  const emptyState = (): EmptyStateProps => {
    if (status === "failed")
      return failedEmptyState(
        "We could not load your plans",
        <Button size="sm" variant="secondary" onClick={reload}>
          Try again
        </Button>
      );

    if (search.trim()) return searchEmptyState("plans");

    // A brand with nothing on offer yet, which is where every workspace starts:
    // the first-run welcome rather than a list reporting a result.
    return {
      variant: "welcome",
      illustration: <FirstPlanMark aria-hidden className="w-11 h-11" />,
      title: "Add the plans you offer",
      description: "Your plans will show up once you create them.",
      highlights: {
        title: "With plans, you can",
        items: [
          "Set clear pricing for every plan you offer.",
          "Define how long each plan runs so clients know what to expect.",
        ],
      },
      action: (
        <Button
          size="md"
          startIcon={<Plus className="w-4 h-4" />}
          onClick={() => setEditing(null)}
        >
          Create plan
        </Button>
      ),
    };
  };

  /**
   * Creates or saves, and takes the row from the response rather than from the
   * draft — the backend sets the id, the created date and the status, and on
   * an edit it is also what says which of the sent changes it kept.
   *
   * The dialog stays open on a failure, which the hook has already reported:
   * closing it would throw away a filled-in form over a request that can be
   * retried by pressing the same button again.
   */
  const handleSubmit = async (draft: NewBrandPlan) => {
    if (editing) {
      const saved = await update(editing, draft);
      if (!saved) return;

      setPlans((current) =>
        current.map((plan) => (plan.id === saved.id ? saved : plan))
      );

      // Reading what was saved, so a second edit does not start by finding
      // the card again.
      stepBackTo(saved);
    } else {
      const saved = await create(draft);
      if (!saved) return;

      setPlans((current) => [saved, ...current]);
      // A live search or a later page would hide the row just added.
      setSearch("");
      setPage(1);
      setShown(CARDS_PER_BATCH);
    }
    setEditing(undefined);
  };

  /**
   * The listing change itself, whichever of the two confirmations asked for
   * it: the row rebuilt from what the server answers with, so the pill
   * follows the backend's `is_active` rather than the button that was
   * pressed. `null` back means the call failed — the hook has already
   * reported it, and the caller leaves its question up so the same button is
   * the retry.
   */
  const savePlanActive = async (plan: BrandPlan, active: boolean) => {
    const saved = await setActive(plan, active);
    if (!saved) return null;

    setPlans((current) =>
      current.map((row) => (row.id === saved.id ? saved : row))
    );
    return saved;
  };

  /** Confirmed in the table's dialog, which is what closes on a success. */
  const handleConfirmToggle = async () => {
    if (!pendingToggle) return;

    const saved = await savePlanActive(
      pendingToggle,
      pendingToggle.status !== "Active"
    );
    if (!saved) return;

    setPendingToggle(null);
  };

  /**
   * Confirmed inside the phone's sheet, which stays open on the saved plan
   * rather than closing: the pill is the answer to what was just asked, and a
   * sheet that vanished would leave it to be found again in the list.
   */
  const handleSetActive = async (plan: BrandPlan, active: boolean) => {
    const saved = await savePlanActive(plan, active);
    if (!saved) return;

    setSelected(saved);
  };

  // Which way the confirmation is pointing. `pendingToggle` is null while it
  // is shut, which reads as Activate — nothing is on screen to see it.
  const deactivating = pendingToggle?.status === "Active";

  /** The heading, and the way to add a plan. The two of them are a row of
   *  their own on a phone with the search under it, and share one row with the
   *  search from `sm` up. */
  const heading = (
    <h2
      className={
        isMobile
          ? "text-[16px] font-medium text-ink"
          : "text-[18px] 3xl:text-[20px] font-medium text-[#111C2D]"
      }
    >
      {/* No count until the list is in: a "(0)" beside a heading that is
          still loading reads as an answer rather than as a blank. */}
      Total plans{status === "ready" ? ` (${plans.length})` : ""}
    </h2>
  );

  const addButton = (
    <AddAction
      label="Add plan"
      onClick={() => setEditing(null)}
      className="flex-shrink-0"
    />
  );

  return (
    // One card holding the lot from `md` up. On a phone there is no card: the
    // plans are cards of their own, and a border around a column of bordered
    // cards is a box inside a box that costs a phone's width twice over. So
    // the tab is only a column there and the plans sit on the page's own
    // background — the shape the Revenue list already takes.
    <section
      className={`lg:flex-1 lg:min-h-0 flex flex-col lg:overflow-hidden ${
        isMobile
          ? "flex-1 min-h-0 overflow-hidden"
          : "rounded-md border border-line bg-white"
      }`}
    >
      {isMobile ? (
        <>
          <div className="flex-shrink-0 flex items-center justify-between gap-3 pb-3">
            {heading}
            {addButton}
          </div>

          {/* A line of its own at the full width of the screen — see
              `MobileSearchField`, which is the same field the roster searches
              with. */}
          <div className="flex-shrink-0 pb-3">
            <MobileSearchField
              value={search}
              onChange={handleSearchChange}
              placeholder="Search plans"
            />
          </div>
        </>
      ) : (
        <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 sm:p-6">
          {heading}

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="relative w-full sm:w-[260px]">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-subtle" />
              <input
                type="search"
                value={search}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder="Search"
                aria-label="Search plans"
                className="w-full rounded-md border border-line py-2 pl-9 pr-3 text-[14px] text-ink placeholder:text-ink-subtle focus:outline-none focus:border-brand"
              />
            </div>

            {addButton}
          </div>
        </div>
      )}

      {/* Said as soon as the request goes out, whether or not anything is
          drawn for it: this line is not seen, so it costs nothing to be early,
          and the skeleton rows it stands for are hidden from screen readers —
          they are the shape of an answer rather than the answer. */}
      {status === "loading" && (
        <p role="status" className="sr-only">
          Loading your plans…
        </p>
      )}

      {/* The phone has no pages under the list — walking one there is
          scrolling — so it grows in batches out of the plans already in hand.
          The table keeps its pagination. */}
      {isMobile ? (
        <PlanCards
          plans={visible}
          phase={phase}
          empty={emptyState()}
          selected={selected}
          onSelect={setSelected}
          onEdit={setEditing}
          saving={saving}
          onSetActive={handleSetActive}
          hasMore={hasMore}
          onEndReached={showMore}
        />
      ) : (
        <>
          <PlansTable
            plans={visible}
            phase={phase}
            empty={emptyState()}
            onEdit={setEditing}
            onToggleStatus={setPendingToggle}
          />

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setPage}
            resultCount={visible.length}
          />
        </>
      )}

      <PlanFormModal
        open={editing !== undefined}
        plan={editing}
        suggestions={suggestions.values}
        durations={durations.options}
        saving={saving}
        // One step back rather than out to the list: on a phone an edit is
        // opened from the plan's sheet, so leaving it without saving lands on
        // that sheet again. A create has nothing behind it — it was opened
        // from the header's button, and `editing` is `null` for it.
        onClose={() => {
          stepBackTo(editing ?? null);
          setEditing(undefined);
        }}
        onSubmit={handleSubmit}
      />

      {/* The table's confirmation, and only the table's — a phone asks the
          same question inside the plan's own sheet, where the plan it is
          about is already on screen. Both directions are confirmed, not just
          the one that takes the plan down: activating puts a price in front
          of clients, which is not something to do by clipping the wrong
          row's menu. */}
      <Modal
        open={!!pendingToggle}
        title={deactivating ? "Deactivate plan" : "Activate plan"}
        onClose={() => setPendingToggle(null)}
        className="max-w-[440px]"
        footer={
          <>
            <Button
              variant="secondary"
              size="md"
              onClick={() => setPendingToggle(null)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant={deactivating ? "danger" : "primary"}
              size="md"
              onClick={handleConfirmToggle}
              loading={saving}
            >
              {deactivating ? "Deactivate" : "Activate"}
            </Button>
          </>
        }
      >
        <p className="text-[14px] 3xl:text-[15px] text-ink-muted">
          {pendingToggle?.name} ({pendingToggle?.duration}){" "}
          {deactivating
            ? "This plan will be hidden until it is activated again."
            : "This plan will be available after activation."}
        </p>
      </Modal>
    </section>
  );
};

export default BrandPlansTab;
